import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	LoginPath,
	ReferralTokenQueryParams,
	ReferralTokenType,
} from "../components/Login/constants/TokenReferralConstants";
import { ReferralToken } from "../components/Login/types/ReferralToken.types";
import {
	clearReferralToken as clearReferralTokenInState,
	setReferralToken,
} from "newui/features/Auth/authSlice";
import { selectReferralToken } from "../authSelectors";

export interface UseReferralToken {
	referralToken: ReferralToken;
	clearReferralToken: () => void;
	setLoginPathChosen: (pathChosen: LoginPath) => void;
}

/**
 * Hook to detect and parse login tokens from URL query parameters
 * Place this on your login screen component
 *
 * Supports multiple token types:
 * - SharedProfile: ?sc=token&company=id
 * - Rfx: ?rfx=token (add when needed)
 * - RfxApproval: ?approval=token (add when needed)
 */
export function useReferralToken(): UseReferralToken {
	const dispatch = useDispatch();
	const location = window.location;
	const referralToken = useSelector(selectReferralToken);

	useEffect(() => {
		if (!location?.search) {
			return;
		}

		// if we have already created a referral token, then do not process again
		if (!!referralToken) {
			return;
		}

		const params = new URLSearchParams(location.search);

		const tokenType = isNaN(
			Number.parseInt(params.get(ReferralTokenQueryParams.TokenType))
		)
			? null
			: Number.parseInt(params.get(ReferralTokenQueryParams.TokenType));

		// Check for SharedProfile token
		const sharedProfileToken = params.get(
			ReferralTokenQueryParams.SharedProfile
		);
		if (sharedProfileToken) {
			const companyId = params.get("company");

			const referralToken: ReferralToken = {
				pathChosen: LoginPath.NoPathChosen,
				tokenType: ReferralTokenType.SharedProfile,
				tokenValue: sharedProfileToken,
				metadata: companyId ? { company: companyId } : undefined,
			};

			dispatch(setReferralToken(referralToken));
			return;
		}

		// Check for Rfx token (when TokenType.Rfx value is defined)
		const rfxToken = params.get(ReferralTokenQueryParams.Rfx);
		if (rfxToken) {
			const referralToken: ReferralToken = {
				pathChosen: LoginPath.NoPathChosen,
				tokenType: tokenType ?? ReferralTokenType.Rfx,
				tokenValue: rfxToken,
			};
			dispatch(setReferralToken(referralToken));
			return;
		}
	}, [location.search, dispatch, referralToken]);

	const clearReferralToken = useCallback(() => {
		if (referralToken) {
			dispatch(clearReferralTokenInState());
		}
	}, [dispatch, referralToken]);

	const setLoginPathChosen = useCallback(
		(pathChosen: LoginPath) => {
			if (referralToken) {
				dispatch(
					setReferralToken({
						...referralToken,
						pathChosen,
					})
				);
			}
		},
		[referralToken, dispatch]
	);

	return { referralToken, clearReferralToken, setLoginPathChosen };
}
